"use client";

import { useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { signInUser } from '@/lib/auth-utils';

export default function SignIn() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get('callbackUrl') || 'https://contracts.saht.store';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Add debugging information
      console.log('Environment:', process.env.NODE_ENV);
      console.log('Starting sign-in process');
      
      const result = await signInUser(email, password, callbackUrl);
      console.log('Sign-in process completed with result:', result.success ? 'success' : 'failure');
      
      if (!result.success) {
        console.error('Sign-in failed:', result.error);
        setError(result.error || 'Authentication failed');
        setLoading(false);
        return;
      }
      
      // NextAuth builds result.url from NEXTAUTH_URL, which is the production
      // domain in every environment — so on a staging (preview) deployment or
      // in local dev a successful sign-in would bounce to the live site. On
      // those hosts stay where we are and follow the relative callback path.
      // Production behaviour is unchanged.
      const stayOnThisHost =
        process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview' ||
        process.env.NODE_ENV === 'development';
      const destination =
        stayOnThisHost && callbackUrl.startsWith('/') ? callbackUrl : result.url;

      // Explicitly log before redirect to help debug production issues
      console.log('Sign-in successful, redirecting to:', destination);

      // Force a small delay before redirect for stability
      setTimeout(() => {
        // Use window.location for more reliable redirect in production
        window.location.href = destination;
      }, 100);
    } catch (err) {
      console.error('Sign-in error:', err);
      setError('An unexpected error occurred');
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight">
            Sign in to your account
          </h2>
        </div>
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <input type="hidden" name="callbackUrl" value={callbackUrl} />
          <div className="-space-y-px rounded-md shadow-sm">
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="relative block w-full rounded-t-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 px-3"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="relative block w-full rounded-b-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 px-3"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-indigo-400"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          <div className="flex items-center justify-center">
            <div className="text-sm">
              Don't have an account?{' '}
              <Link
                href="/auth/register"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                Register
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}